<script setup lang="ts">
// The topbar AI-credits popover — v3 `features/credits/components/CreditPanel.vue`, layout for
// layout: a trial or balance hero, the segmented spend bar with its hover legend, the add-on lots,
// the consumption rates and the Upgrade / Manage pair. The settings page keeps its own richer
// `CreditBalanceOverview`; the popover never borrowed it in v3 and does not here.
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import {
  CONSUMPTION_RATES,
  formatResetDate,
  getPlanMeta,
  type CreditFeatureTone,
} from '../service/creditPresentation'
import { useCreditStore } from '../stores/creditStore'

import orpheusLogoUrl from '@/shared/assets/logos/orpheus_logo.png'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Icon, type UiTone } from '@/shared/ui'

const emit = defineEmits<{ close: [] }>()
const store = useCreditStore()
const router = useRouter()
const { t } = useI18n()
const { formatInteger } = useFormat()
const canManageCredits = computed(() => router.hasRoute('SettingsCredits'))
const panelTitle = computed(() =>
  store.isTrial ? t('credits.panel.freeTrial') : t('credits.panel.aiCredits'),
)

/** Static class strings per tone, so Tailwind sees every utility at build time. */
const FEATURE_ICON_CLASS: Record<CreditFeatureTone, string> = {
  orpheus: 'bg-credit-orpheus-soft text-orpheus-brand',
  impact: 'bg-credit-impact-soft text-credit-impact',
  creative: 'bg-credit-creative-soft text-credit-creative',
  unknown: 'bg-credit-unknown-soft text-credit-unknown',
}
const FEATURE_BAR_CLASS: Record<CreditFeatureTone, string> = {
  orpheus: 'bg-orpheus-brand',
  impact: 'bg-credit-impact',
  creative: 'bg-credit-creative',
  unknown: 'bg-credit-unknown',
}
const PLAN_CHIP_CLASS: Record<UiTone, string> = {
  neutral:
    'bg-[color:var(--ui-color-credit-plan-neutral-soft)] text-[color:var(--ui-color-credit-plan-neutral)]',
  info: 'bg-[color:var(--ui-color-credit-plan-info-soft)] text-[color:var(--ui-color-credit-plan-info)]',
  success:
    'bg-[color:var(--ui-color-credit-plan-success-soft)] text-[color:var(--ui-color-credit-plan-success)]',
  warning:
    'bg-[color:var(--ui-color-credit-plan-warning-soft)] text-[color:var(--ui-color-credit-plan-warning)]',
  danger:
    'bg-[color:var(--ui-color-credit-plan-danger-soft)] text-[color:var(--ui-color-credit-plan-danger)]',
}

const planLabel = computed(() => {
  const meta = getPlanMeta(store.plan)
  return meta.labelKey ? t(meta.labelKey) : (store.plan ?? '')
})
const planChipClass = computed(() => PLAN_CHIP_CLASS[getPlanMeta(store.plan).toneToken])

const hint = computed(() => {
  if (store.slotState === 'depleted') {
    return { text: t('credits.panel.outOfCredits'), class: 'bg-danger-soft text-danger' }
  }
  if (store.slotState === 'low') {
    return { text: t('credits.panel.runningLow'), class: 'bg-warning-soft text-warning' }
  }
  return null
})

const trialEndLabel = computed(() => formatResetDate(store.trialExpiresAt))
const addonExpiresLabel = computed(() => formatResetDate(store.addonExpiresAt))

/** Spend segments as % of `total`, clamped so the bar never overflows. */
const segments = computed(() => {
  const total = store.total
  if (total <= 0) return []
  let allocated = 0
  return store.spendBreakdown
    .filter((segment) => segment.spent > 0)
    .map((segment) => {
      const width = Math.max(0, Math.min(100 - allocated, (segment.spent / total) * 100))
      allocated += width
      return { ...segment, width: `${width}%` }
    })
})

/** Every feature with spend — including slivers the bar rounds to nothing. */
const legend = computed(() => store.spendBreakdown.filter((segment) => segment.spent > 0))

/** Legacy parity (`barHover`): the per-feature breakdown only shows while the bar is hovered. */
const barHover = ref(false)

function featureLabel(segment: (typeof legend.value)[number]): string {
  return segment.presentation.unknownLabel ?? t(segment.presentation.labelKey)
}

function openUpgrade(): void {
  emit('close')
  store.openUpgrade()
}

function closePanel(): void {
  emit('close')
}

function manageCredits(): void {
  if (!canManageCredits.value) return
  emit('close')
  void router.push({ name: 'SettingsCredits' })
}
</script>

<template>
  <section
    class="credit-panel w-[min(22.5rem,_calc(100vw_-_2rem))] overflow-hidden border border-border-subtle rounded-xl bg-surface text-fg shadow-pop-strong font-sans max-[24rem]:w-[calc(100vw_-_2rem)]"
    :data-state="store.slotState"
    data-testid="credit-panel"
  >
    <header class="credit-panel__header flex items-start justify-between px-5 pt-5">
      <span
        class="credit-panel__title text-[0.6875rem] font-bold uppercase tracking-widest"
        :class="store.isTrial ? 'text-wizard-accent' : 'text-credit-heading'"
        data-testid="credit-panel-title"
        >{{ panelTitle }}</span
      >
      <button
        type="button"
        class="credit-panel__dismiss -mt-2 -mr-2 inline-flex size-8 items-center justify-center rounded-md border-0 bg-transparent p-0 text-credit-caption outline-none cursor-pointer transition-colors hover:text-muted focus-visible:shadow-[0_0_0_2px_var(--ui-color-ring)]"
        :aria-label="t('credits.panel.dismiss')"
        data-testid="credit-panel-dismiss"
        @click="closePanel"
      >
        <Icon name="x" :size="14" aria-hidden="true" />
      </button>
    </header>

    <!-- Trial hero -->
    <div v-if="store.isTrial" class="credit-panel__hero px-5 pt-2 pb-4">
      <p class="m-0 mb-1 text-3xl font-bold text-fg">
        {{ t('credits.panel.trialDaysLeft', store.trialDaysRemaining ?? 0) }}
      </p>
      <p class="m-0 text-sm leading-relaxed text-muted">
        {{ t('credits.panel.trialFreeUntil') }}
        <span class="font-medium text-fg">{{ trialEndLabel }}</span
        >{{ t('credits.panel.trialAfter') }}
      </p>
      <p v-if="store.remaining" class="m-0 mt-2 text-xs text-credit-caption">
        <span class="font-semibold text-muted" data-testid="credit-remaining">{{
          formatInteger(store.remaining)
        }}</span>
        {{ t('credits.panel.trialRemaining') }}
      </p>
    </div>

    <!-- Credit hero + segmented bar -->
    <div v-else class="credit-panel__hero px-5 pt-2 pb-4">
      <p class="m-0 mb-2 flex flex-wrap items-baseline gap-x-1.5">
        <span
          class="text-3xl font-bold"
          :class="store.isDepleted ? 'text-danger' : 'text-fg'"
          data-testid="credit-remaining"
          >{{ formatInteger(store.remaining) }}</span
        >
        <span class="text-sm text-credit-caption">
          {{ t('credits.panel.creditsLeft') }}
          <span class="text-primary">{{
            t('credits.slot.of', { total: formatInteger(store.total) })
          }}</span>
        </span>
        <span
          v-if="hint"
          class="rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold"
          :class="hint.class"
          data-testid="credit-panel-hint"
          >{{ hint.text }}</span
        >
      </p>

      <p class="m-0 mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-credit-caption">
        <span
          v-if="store.plan"
          class="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[0.6875rem]"
          :class="planChipClass"
          data-testid="credit-panel-plan"
        >
          <span class="font-semibold">{{ planLabel }}</span>
          <span class="font-normal opacity-80">{{
            t('credits.panel.planCredits', { count: formatInteger(store.planCredits) })
          }}</span>
          <template v-if="store.resetDateLabel">
            <span class="opacity-40" aria-hidden="true">·</span>
            <span class="font-normal opacity-70">{{
              t('credits.panel.expires', { date: store.resetDateLabel })
            }}</span>
          </template>
        </span>
        <template v-else>{{
          t('credits.slot.of', { total: formatInteger(store.total) })
        }}</template>
        <span
          v-if="store.buckets.addon"
          class="inline-flex items-center gap-1 rounded-md bg-credit-addon-soft px-2 py-0.5 text-[0.6875rem] font-semibold text-credit-addon"
        >
          {{ t('credits.panel.addonChip', { count: formatInteger(store.addon.total) }) }}
          <template v-if="addonExpiresLabel">
            <span class="opacity-40" aria-hidden="true">·</span>
            <span class="font-normal opacity-80">{{
              t('credits.panel.expires', { date: addonExpiresLabel })
            }}</span>
          </template>
        </span>
      </p>

      <div
        class="credit-panel__spend relative mt-4"
        @mouseenter="barHover = true"
        @mouseleave="barHover = false"
      >
        <div class="flex h-2.5 w-full overflow-hidden rounded-full bg-credit-track">
          <span
            v-for="segment in segments"
            :key="segment.key"
            class="h-full"
            :class="FEATURE_BAR_CLASS[segment.presentation.toneToken]"
            :style="{ width: segment.width }"
          />
        </div>

        <Transition
          enter-active-class="transition duration-150 ease-out"
          enter-from-class="translate-y-1 opacity-0"
          enter-to-class="translate-y-0 opacity-100"
          leave-active-class="transition duration-100 ease-in"
          leave-from-class="translate-y-0 opacity-100"
          leave-to-class="translate-y-1 opacity-0"
        >
          <div
            v-if="barHover"
            class="credit-panel__legend absolute top-full left-0 z-20 mt-2 w-52 rounded-xl border border-border-subtle bg-surface p-3 shadow-pop-strong"
          >
            <template v-if="legend.length">
              <div
                v-for="segment in legend"
                :key="segment.key"
                class="flex items-center gap-2 py-1"
              >
                <span
                  class="size-2 flex-none rounded-full"
                  :class="FEATURE_BAR_CLASS[segment.presentation.toneToken]"
                  aria-hidden="true"
                />
                <span class="flex-1 text-sm text-muted">{{ featureLabel(segment) }}</span>
                <span class="text-sm font-semibold text-fg">{{
                  formatInteger(segment.spent)
                }}</span>
              </div>
              <div
                class="mt-2 flex items-center justify-between border-t border-t-border-subtle pt-2"
              >
                <span class="text-xs text-credit-caption">{{ t('credits.panel.used') }}</span>
                <span class="text-xs font-bold text-fg">{{
                  t('credits.panel.usedOf', {
                    spent: formatInteger(store.spentTotal),
                    total: formatInteger(store.total),
                  })
                }}</span>
              </div>
            </template>
            <p v-else class="m-0 text-xs text-credit-caption">{{ t('credits.panel.noSpend') }}</p>
          </div>
        </Transition>
      </div>

      <div
        v-if="store.isDepleted"
        class="mt-3 rounded-lg bg-danger-soft px-3 py-2.5"
        data-testid="credit-depleted-alert"
      >
        <p class="m-0 text-sm font-medium text-danger">{{ t('credits.panel.depletedLocked') }}</p>
      </div>
    </div>

    <!-- Add-on credits (per-lot detail) -->
    <section
      v-if="store.addonLots.length"
      class="credit-panel__section border-t border-t-border-subtle px-5 py-4"
    >
      <h3 class="m-0 mb-2 text-[0.6875rem] font-bold uppercase tracking-widest text-credit-caption">
        {{ t('credits.panel.addonCredits') }}
      </h3>
      <ul class="m-0 flex list-none flex-col gap-2 p-0">
        <li
          v-for="(lot, index) in store.addonLots"
          :key="`${lot.expires_at ?? 'open'}:${index}`"
          class="flex items-baseline justify-between gap-3"
        >
          <span class="min-w-0 flex-1">
            <span class="block truncate text-sm font-semibold text-fg">{{
              lot.package || t('credits.panel.addonFallback')
            }}</span>
            <span v-if="lot.expires_at" class="block text-xs text-credit-caption">{{
              t('credits.panel.expires', { date: formatResetDate(lot.expires_at) })
            }}</span>
          </span>
          <span class="flex-none text-sm text-muted">
            <span class="font-semibold text-fg">{{ formatInteger(lot.remaining) }}</span
            >{{ t('credits.panel.lotOf', { amount: formatInteger(lot.amount) }) }}
          </span>
        </li>
      </ul>
    </section>

    <!-- What's included (consumption rates) -->
    <section class="credit-panel__section border-t border-t-border-subtle px-5 py-4">
      <h3 class="m-0 mb-2 text-[0.6875rem] font-bold uppercase tracking-widest text-credit-caption">
        {{ t('credits.panel.whatsIncluded') }}
      </h3>
      <ul class="credit-panel__rates m-0 flex list-none flex-col p-0">
        <li v-for="rate in CONSUMPTION_RATES" :key="rate.labelKey">
          <RouterLink
            class="credit-panel__feature-link group flex items-center gap-3 rounded-lg px-2 py-2.5 text-fg no-underline outline-none transition-colors hover:bg-surface-sunken focus-visible:shadow-[0_0_0_2px_var(--ui-color-ring)]"
            :to="{ name: rate.route }"
            @click="closePanel"
          >
            <span
              class="credit-panel__feature-icon flex size-9 flex-none items-center justify-center rounded-lg"
              :class="FEATURE_ICON_CLASS[rate.toneToken]"
            >
              <img
                v-if="rate.toneToken === 'orpheus'"
                :src="orpheusLogoUrl"
                class="size-5 object-contain"
                alt=""
                data-testid="credit-panel-orpheus-logo"
              />
              <Icon v-else :name="rate.icon" :size="14" aria-hidden="true" />
            </span>
            <span class="credit-panel__feature-copy min-w-0 flex-1">
              <span class="block text-sm font-semibold text-orpheus-text">{{
                t(rate.labelKey)
              }}</span>
              <span class="block text-xs text-credit-caption">{{ t(rate.rateKey) }}</span>
            </span>
            <Icon
              class="credit-panel__feature-chevron text-credit-chevron transition-colors group-hover:text-credit-caption"
              name="chevron-right"
              :size="11"
              aria-hidden="true"
            />
          </RouterLink>
        </li>
      </ul>
    </section>

    <footer class="credit-panel__actions flex items-center gap-2 px-5 pb-5">
      <button
        type="button"
        class="credit-panel__upgrade flex flex-1 cursor-pointer items-center justify-center gap-2 h-10 rounded-lg border-0 bg-primary font-sans text-sm font-semibold text-primary-fg transition-colors hover:bg-primary-hover focus-visible:shadow-[0_0_0_2px_var(--ui-color-ring)] outline-none"
        @click="openUpgrade"
      >
        {{ t('credits.panel.upgrade') }}
        <Icon name="arrow-right" :size="12" aria-hidden="true" />
      </button>
      <button
        v-if="canManageCredits"
        type="button"
        class="credit-panel__manage flex-none cursor-pointer h-10 rounded-lg border border-border bg-surface px-4 font-sans text-sm font-semibold text-muted transition-colors hover:bg-surface-sunken focus-visible:shadow-[0_0_0_2px_var(--ui-color-ring)] outline-none"
        @click="manageCredits"
      >
        {{ t('credits.panel.manage') }}
      </button>
    </footer>
  </section>
</template>
